import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Building2, ArrowLeft, Check, Loader2, Briefcase, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { roles as mockRoles } from '../data/mock';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchRole();
  }, [id]);

  const fetchRole = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/roles/${id}`);
      setRole(response.data);
      
      // Check if user has already applied
      if (token) {
        try {
          const appRes = await axios.get(`${API}/applications/check/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setHasApplied(appRes.data.has_applied);
        } catch (e) {}
      }
    } catch (error) {
      // Use mock data as fallback
      const mockRole = mockRoles.find(r => r.id === id);
      setRole(mockRole || null);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast({ title: 'Please log in to apply', variant: 'destructive' });
      navigate('/login');
      return;
    }

    if (!user.onboarding_completed) {
      toast({ title: 'Please complete your profile first', variant: 'destructive' });
      navigate('/onboarding');
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/applications`, {
        role_id: id,
        cover_letter: coverLetter,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHasApplied(true);
      setShowApplyModal(false);
      toast({ title: 'Application submitted!', description: 'The employer will review your profile.' });
    } catch (error) {
      toast({ 
        title: 'Application failed', 
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive' 
      });
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b">
          <Navbar variant="light" />
        </div>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">Role not found</h1>
          <Button onClick={() => navigate('/roles')}>Browse all roles</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <Navbar variant="light" />
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Back Button */}
        <button
          onClick={() => navigate('/roles')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to roles
        </button>

        {/* Role Header */}
        <div className="bg-white rounded-2xl p-8 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">{role.title}</h1>
              <div className="flex items-center gap-2 text-gray-500 mb-4">
                <Building2 className="w-4 h-4" />
                <span>{role.company}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="gap-1">
                  <Briefcase className="w-3 h-3" />
                  {role.type}
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <MapPin className="w-3 h-3" />
                  {role.location}
                </Badge>
                <Badge variant="secondary" className="gap-1">
                  <DollarSign className="w-3 h-3" />
                  {role.payRange || role.pay_range}
                </Badge>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {hasApplied ? (
                <Button disabled className="bg-green-100 text-green-700 hover:bg-green-100">
                  <Check className="w-4 h-4 mr-2" />
                  Applied
                </Button>
              ) : (
                <Button 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => setShowApplyModal(true)}
                >
                  Apply Now
                </Button>
              )}
              <span className="text-sm text-gray-400 text-center">{role.hiredRecently || role.hired_recently || 0} hired recently</span>
            </div>
          </div>
        </div>

        {/* Role Details */}
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">About this role</h2>
          <p className="text-gray-600 mb-6">{role.description}</p>

          <h2 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            {(role.requirements || []).map((req, i) => (
              <li key={i}>{req}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Apply Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply for {role.title}</DialogTitle>
            <DialogDescription>
              Your profile will be shared with the employer. Add a cover letter to stand out.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Write a brief cover letter (optional)..."
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              className="h-32"
            />
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={handleApply}
                disabled={applying}
              >
                {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Application
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default RoleDetail;
